import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function ModalidadButton({ modalidad, isSelected, anySelected, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  let className =
    "px-2 py-1 rounded font-medium transition-all duration-150 border cursor-pointer select-none ";

  if (isSelected) {
    className += `${modalidad.bgColor} ${modalidad.textColor} border-transparent scale-105 shadow-md text-xs`;
  } else if (anySelected) {
    className += "bg-aw-bluedark text-gray-100 border-gray-700 text-[11px]";
  } else if (isHovered) {
    className += `${modalidad.bgColor} ${modalidad.textColor} border-transparent opacity-80 scale-90 text-xs`;
  } else {
    className += `${modalidad.bgColor} ${modalidad.textColor} border-transparent text-xs`;
  }

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={className}
    >
      {modalidad.label}
    </button>
  );
}

import AplicarSeleccion from "@/components/ViewEventoOwnerEmpresa/DataTableClients/OpcionesInvitacion/AplicarSeleccion";
import {
  responderSolicitudesBulk,
  getSolicitudesDeEvento,
} from "@/features/invitacion/invitacionThunks";
import { getColorByEstadoInvitacion } from "@/utils/invitacionUtils";

const MODALIDADES = [
  { key: "gratis", label: "Free", bgColor: "bg-aw-green", textColor: "text-gray-900" },
  { key: "con_descuento", label: "Con Dscto", bgColor: "bg-yellow-500", textColor: "text-gray-900" },
  { key: "pago_total", label: "Con Pago", bgColor: "bg-purple-500", textColor: "text-gray-50" },
  { key: "gratis_total", label: "Free Total", bgColor: "bg-aw-bluegray", textColor: "text-gray-50" },
  { key: "rechazar", label: "Rechazar", bgColor: "bg-red-700", textColor: "text-white" },
];

function InstagramCellRenderer({ value }) {
  if (!value) return <span className="text-gray-600 text-xs">—</span>;

  // Extrae el username si viene como URL completa o con @
  const username = value
    .replace(/https?:\/\/(www\.)?instagram\.com\/?/i, "")
    .replace(/^@/, "")
    .replace(/\/$/, "");

  return (
    <a
      href={`https://instagram.com/${username}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-aw-green/85 hover:text-aw-green hover:underline text-xs"
    >
      @{username}
    </a>
  );
}

function formatFecha(fechaStr) {
  if (!fechaStr) return "";
  const d = new Date(fechaStr);
  if (isNaN(d.getTime())) return fechaStr;
  return d.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFechaNacimiento(fechaStr) {
  if (!fechaStr) return "—";
  const birth = new Date(fechaStr);
  if (isNaN(birth.getTime())) return "—";
  const now = new Date();
  let age = now.getFullYear() - birth.getUTCFullYear();
  const m = now.getMonth() - birth.getUTCMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getUTCDate())) age--;
  const day = String(birth.getUTCDate()).padStart(2, "0");
  const month = String(birth.getUTCMonth() + 1).padStart(2, "0");
  const year = birth.getUTCFullYear();
  return { date: `${day}-${month}-${year}`, age };
}

export default function TabInvitacionesEvento({ solicitudes, uidEvento }) {
  const dispatch = useDispatch();

  const [decisiones, setDecisiones] = useState({});
  const [isApplying, setIsApplying] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Ordenar: pendientes primero (mas antiguas arriba), el resto por fecha descendente
  const solicitudesOrdenadas = useMemo(() => {
    if (!solicitudes || solicitudes.length === 0) return [];

    const pendientes = solicitudes
      .filter((s) => s.estado === "pendiente")
      .sort(
        (a, b) =>
          new Date(a.fechaCreacion || a.createdAt || 0) -
          new Date(b.fechaCreacion || b.createdAt || 0)
      );

    const resto = solicitudes
      .filter((s) => s.estado !== "pendiente")
      .sort(
        (a, b) =>
          new Date(b.fechaCreacion || b.createdAt || 0) -
          new Date(a.fechaCreacion || a.createdAt || 0)
      );

    return [...pendientes, ...resto];
  }, [solicitudes]);

  const handleSeleccionarModalidad = (uid, modalidad) => {
    setDecisiones((prev) => {
      if (prev[uid] === modalidad) {
        const { [uid]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [uid]: modalidad };
    });
  };

  const handleAplicar = async () => {
    const decisionesArray = Object.entries(decisiones).map(
      ([uid, modalidad]) => ({ uid, modalidad })
    );

    if (decisionesArray.length === 0) return;

    setIsApplying(true);
    setErrorMsg(null);

    try {
      await dispatch(
        responderSolicitudesBulk({ uidEvento: uidEvento, decisiones: decisionesArray })
      ).unwrap();
      await dispatch(getSolicitudesDeEvento({ uidEvento }));
      setDecisiones({});
    } catch (err) {
      const mensaje =
        err?.message || (typeof err === "string" ? err : "Error al aplicar decisiones");
      setErrorMsg(mensaje);
    } finally {
      setIsApplying(false);
    }
  };

  if (!solicitudes || solicitudes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <FontAwesomeIcon
          icon="fa-solid fa-envelope-open-text"
          className="text-5xl mb-4 opacity-30"
        />
        <h4 className="text-lg font-semibold mb-2 text-white">
          Sin solicitudes de invitacion
        </h4>
        <p className="text-center text-sm">
          Aun no hay usuarios que hayan solicitado invitacion para este evento
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-bold text-white">
            Solicitudes de Invitación
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            {solicitudesOrdenadas.length} solicitud
            {solicitudesOrdenadas.length !== 1 ? "es" : ""}
          </p>
        </div>

        <AplicarSeleccion
          onApplySelections={handleAplicar}
          hasSelections={Object.keys(decisiones).length > 0}
          isLoading={isApplying}
        />
      </div>

      {errorMsg && (
        <div className="mb-4 bg-red-500/15 border border-red-500/40 rounded-xl px-4 py-3 text-red-400 text-sm">
          {errorMsg}
        </div>
      )}

      {/* Tabla de solicitudes */}
      <div className="w-full overflow-x-auto rounded-xl border border-gray-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-aw-bluedark-75 text-gray-300">
            <tr>
              <th className="px-4 py-3 font-semibold">Usuario</th>
              <th className="px-4 py-3 font-semibold">Instagram</th>
              <th className="px-4 py-3 font-semibold">Tipo Ticket</th>
              <th className="px-4 py-3 font-semibold">Embajador</th>
              <th className="px-4 py-3 font-semibold">Nacimiento</th>
              <th className="px-4 py-3 font-semibold">Fecha Solicitud</th>
              <th className="px-4 py-3 font-semibold">Estado Actual</th>
              <th className="px-4 py-3 font-semibold">Modalidad</th>
            </tr>
          </thead>
          <tbody>
            {solicitudesOrdenadas.map((sol) => {
              const colores = getColorByEstadoInvitacion(sol.estado);
              const esPendiente = sol.estado === "pendiente";
              const mDecision = decisiones[sol.uid];

              return (
                <tr
                  key={sol.uid}
                  className="border-t border-gray-700 bg-aw-bluedark hover:bg-aw-bluedark/80 transition"
                >
                  {/* Usuario */}
                  <td className="px-4 py-3">
                    <p className="text-white font-semibold">
                      {sol.usuarioNombre} {sol.usuarioApellido}
                    </p>
                    {sol.usuarioAlias && (
                      <p className="text-aw-green text-xs">
                        @{sol.usuarioAlias}
                      </p>
                    )}
                  </td>
                  {/* Instagram */}
                  <td className="px-4 py-3">
                    <InstagramCellRenderer value={sol.instagram} />
                  </td>
                  {/* Tipo de ticket */}
                  <td className="px-4 py-3 text-gray-300">
                    {sol.tipoTicketNombre || "-"}
                  </td>

                  {/* Embajador */}
                  <td className="px-4 py-3">
                    {sol.embajadorNombre ? (
                      <div>
                        <p className="text-aw-green text-xs font-semibold">{sol.embajadorNombre}</p>
                        {sol.embajadorAlias && (
                          <p className="text-gray-500 text-[11px]">@{sol.embajadorAlias}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-600 text-xs">--</span>
                    )}
                  </td>

                  {/* Fecha nacimiento */}
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {(() => {
                      const fn = formatFechaNacimiento(sol.usuarioFechaNacimiento);
                      if (typeof fn === "string") return fn;
                      return <span>{fn.date} (<span className="text-aw-green">{fn.age}</span>)</span>;
                    })()}
                  </td>

                  {/* Fecha */}
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {formatFecha(sol.fechaCreacion || sol.createdAt)}
                  </td>

                  {/* Estado */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-semibold ${colores.badge}`}
                    >
                      {colores.label}
                    </span>
                  </td>

                  {/* Modalidad */}
                  <td className="px-4 py-3">
                    {esPendiente ? (
                      <div className="flex flex-wrap gap-1">
                        {MODALIDADES.map((m) => (
                          <ModalidadButton
                            key={m.key}
                            modalidad={m}
                            isSelected={mDecision === m.key}
                            anySelected={!!mDecision}
                            onClick={() =>
                              handleSeleccionarModalidad(sol.uid, m.key)
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-xs italic">
                        Ya respondida
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
